// src/hooks/useLastSafeRoute.js
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const KEY_STACK = "pdx.safeRouteStack.v1";

/**
 * Normalise known invalid recording subpaths produced by relative navigation.
 *
 * Bug pattern:
 *   /recording/<variant>/camera/guide  (invalid)
 *
 * Correct:
 *   /recording/<variant>/guide
 */
function splitPathAndSuffix(path) {
  const queryIndex = path.indexOf("?");
  const hashIndex = path.indexOf("#");
  const indexes = [queryIndex, hashIndex].filter((index) => index >= 0);
  const suffixIndex = indexes.length ? Math.min(...indexes) : -1;

  if (suffixIndex < 0) return { pathname: path, suffix: "" };

  return {
    pathname: path.slice(0, suffixIndex),
    suffix: path.slice(suffixIndex),
  };
}

function normalizeSafePath(path) {
  if (typeof path !== "string") return path;

  const { pathname, suffix } = splitPathAndSuffix(path);

  // Rewrite the invalid nested guide path
  const m = pathname.match(/^\/recording\/([^/]+)\/camera\/guide\/?$/);
  if (m) return `/recording/${m[1]}/guide${suffix}`;

  return path;
}
/**
 * Returns true if a path is considered unsafe to route "back" into.
 */
export function isUnsafePath(pathname) {
  if (!pathname) return true;
  if (pathname === "/") return true;
  if (pathname.startsWith("/auth")) return true;
  if (pathname === "/review-metadata") return true;
  return false;
}

function isRecordingPath(path) {
  return typeof path === "string" && path.startsWith("/recording/");
}

function isRecordingGuidePath(path) {
  if (typeof path !== "string") return false;
  const { pathname } = splitPathAndSuffix(path);
  return /^\/recording\/[^/]+\/guide\/?$/.test(pathname);
}

function getPathname(path) {
  if (typeof path !== "string") return "";
  return splitPathAndSuffix(path).pathname;
}

function isResultsListPath(path) {
  return getPathname(path) === "/results";
}

function isResultDetailPath(path) {
  return getPathname(path) === "/result";
}

function isReviewMetadataPath(path) {
  return getPathname(path) === "/review-metadata";
}

function readStack() {
  try {
    const raw = sessionStorage.getItem(KEY_STACK);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((x) => typeof x === "string")
      : [];
  } catch {
    return [];
  }
}

function writeStack(stack) {
  try {
    sessionStorage.setItem(KEY_STACK, JSON.stringify(stack));
  } catch {
    // ignore
  }
}

/**
 * Track a stack of "safe" routes only.
 * Logging is intentionally minimal and only prints key events.
 */
export function useLastSafeRoute() {
  const location = useLocation();
  const rawPathname = location?.pathname || "/";
  const pathname = normalizeSafePath(rawPathname);
  const search = location?.search || "";

  useEffect(() => {
    const fullPath = normalizeSafePath(`${pathname}${search || ""}`);

    if (isUnsafePath(pathname)) {
      return;
    }

    const stack = readStack().map(normalizeSafePath);
    const last = stack.length ? stack[stack.length - 1] : null;

    if (stack.length === 0 && pathname.startsWith("/recording")) {
      stack.push("/home");
    }

    if (last !== fullPath) {
      stack.push(fullPath);
      if (stack.length > 50) stack.splice(0, stack.length - 50);
      writeStack(stack);
    }
  }, [pathname, search]);
}

/**
 * Returns the previous safe route (one step back). Does NOT mutate.
 */
export function peekPrevSafeRoute(fallback = "/home") {
  const stack = readStack().map(normalizeSafePath);
  const prev = stack.length >= 2 ? stack[stack.length - 2] : null;

  return prev || fallback;
}

/**
 * Pops current and returns previous safe route.
 */
export function popToPrevSafeRoute(fallback = "/home", currentPath = "") {
  const stack = readStack().map(normalizeSafePath);
  const current = normalizeSafePath(currentPath);

  if (stack.length < 2) return fallback;

  stack.pop();

  let dest = stack.length ? stack[stack.length - 1] : fallback;
  dest = normalizeSafePath(dest);

  if (isRecordingGuidePath(current)) {
    while (
      stack.length &&
      (isRecordingPath(dest) || isReviewMetadataPath(dest))
    ) {
      stack.pop();
      dest = stack.length
        ? normalizeSafePath(stack[stack.length - 1])
        : fallback;
    }
  }

  if (isResultsListPath(current) && isResultDetailPath(dest)) {
    while (stack.length && isResultDetailPath(dest)) {
      stack.pop();
      dest = stack.length
        ? normalizeSafePath(stack[stack.length - 1])
        : fallback;
    }
  }

  if (!dest || dest === current) dest = fallback;

  writeStack(stack);

  return dest;
}
